import React from "react";

// External
import styled from "styled-components";
import AniLink from "gatsby-plugin-transition-link/AniLink";

// Internal
import SplashText from "../../components/SplashText";
import { breakpoints, fontFamily, colors, sectionRhythm } from "../../../theme";
import { ALL_RECOMMENDATIONS } from "../../utils/constants/recommendations";
import Button from "../../components/form/Button";
import Image from "../../components/common/Image";
import useMediaQuery from "../../hooks/use-media-query";
import testimonialBgLines from "../../images/testimonial-header-bg-lines.svg";

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    flex-direction: row;
    justify-content: center;
    gap: 60px;
  }
`;

const TitleSection = styled.div`
  text-align: center;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    text-align: left;
    width: 54%;
    max-width: 566px;
    margin: auto 0;
  }
`;

const Title = styled.h1`
  font-family: ${fontFamily.extraBold};
  font-size: 26px;
  line-height: 120%;
  letter-spacing: -1%;
  margin: 0;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 52px;
  }
  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 60px;
  }
`;

const HelperText = styled.p`
  font-family: ${fontFamily.medium};
  font-size: 16px;
  line-height: 140%;
  letter-spacing: 1%;
  color: ${colors.body};
  margin: 8px 0 0;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 20px;
    margin: 0;
  }
`;

const DesktopCta = styled(Button)`
  display: none;
  margin-top: ${sectionRhythm.paddingY.desktop}px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    display: inline-block;
  }
`;

const MobileCta = styled(Button)`
  display: inline-block;
  align-self: center;
  margin-top: ${sectionRhythm.paddingY.mobile}px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    display: none;
  }
`;

const ImagesContainer = styled.div`
  position: relative;
  width: 100%;
  height: 350px;
  max-width: 375px;

  background-image: url(${testimonialBgLines});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 492px;
    height: 457px;
    max-width: unset;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    margin: 0;
  }
`;

const ImageContainer = styled.div`
  position: absolute;
  border-radius: 50%;
  overflow: hidden;
`;

const CenterImageContainer = styled(ImageContainer)`
  height: 140px;
  width: 140px;

  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  @media only screen and (min-width: ${breakpoints.tablet}) {
    height: 185px;
    width: 185px;
  }
`;

const ProfileImage = styled(Image)`
  height: 100%;
  width: 100%;
  object-fit: cover;
  filter: grayscale(100%);
`;

const TestimonialsHeader = () => {
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.desktop} )`);
  const isMobile = !useMediaQuery(`(min-width: ${breakpoints.tablet} )`);
  const selectedRecommendations = ALL_RECOMMENDATIONS.slice(1, 6).reverse();
  const centerRecommendation = ALL_RECOMMENDATIONS[0];

  const defaultImageStyles = [
    { top: "12%", left: "14%", height: "82px", width: "82px" },
    { top: "8%", left: "72%", height: "125px", width: "125px" },
    { top: "41%", left: "1%", height: "107px", width: "107px" },
    { top: "72%", left: "26%", height: "150px", width: "150px" },
    { top: "53%", left: "70%", height: "134px", width: "134px" },
  ];

  const mobileImageStyles = [
    { top: "12%", left: "14%", height: "62px", width: "62px" },
    { top: "8%", left: "72%", height: "94px", width: "94px" },
    { top: "41%", left: "1%", height: "82px", width: "82px" },
    { top: "72%", left: "26%", height: "114px", width: "114px" },
    { top: "53%", left: "70%", height: "100px", width: "100px" },
  ];

  return (
    <HeaderContainer>
      <TitleSection>
        <Title>
          <SplashText variant="accent">They Said It, </SplashText>
          {isDesktop && <br />}
          <span>Not Us.</span>
        </Title>
        <HelperText>
          Here's what our clients tell when the work speaks for itself. No
          gimmicks, no filters - just the raw experiences of people who trusted
          us with their vision and saw it take shape.
        </HelperText>
        <DesktopCta
          forwardedAs={AniLink}
          cover
          bg={colors.black}
          to="/contact-me/"
        >
          See How We Can Help
        </DesktopCta>
      </TitleSection>
      <ImagesContainer>
        <CenterImageContainer>
          <ProfileImage
            src={centerRecommendation.profilePic}
            alt={centerRecommendation.name}
          />
        </CenterImageContainer>

        {selectedRecommendations.map((testimonial, index) => {
          const styles = isMobile
            ? mobileImageStyles[index]
            : defaultImageStyles[index];
          return (
            <ImageContainer key={index} style={{ ...styles }}>
              <ProfileImage
                src={testimonial.profilePic}
                alt={testimonial.name}
              />
            </ImageContainer>
          );
        })}
      </ImagesContainer>
      <MobileCta
        forwardedAs={AniLink}
        cover
        bg={colors.black}
        to="/contact-me/"
      >
        See How We Can Help
      </MobileCta>
    </HeaderContainer>
  );
};

export default TestimonialsHeader;
